// TODO mlockard, this file is declared as side effectful but doing so is preventing most of our tree shaking
/* eslint-disable tree-shaking/no-side-effects-in-initialization */
import { ApolloClient, ApolloProvider } from "@apollo/client";
import {
  AGENT_TYPE_HEADER,
  AGENT_VERSION_HEADER,
  AgentType,
  AppFeatureFlag,
  AuthenticatedUserOrgDetails,
  DEFAULT_FLAGS,
  TRACE_ID_HEADER,
  UnAuthenticatedUserOrgDetails,
  UserOrgDetails,
  createTraceId,
} from "@hex/common";
import { LDClient } from "launchdarkly-js-client-sdk";
import { useLDClient } from "launchdarkly-react-client-sdk";
import MouseTrap from "mousetrap";
import React, {
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Provider as ReduxProvider } from "react-redux";
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  createBrowserRouter,
  useLocation,
} from "react-router";
import { RouterProvider } from "react-router/dom";
import styled from "styled-components";

import { AtomicControllerContext } from "./atomic-operations/AtomicControllerContext.js";
import { connectionManager, createClient } from "./client";
import { KernelCompletionProvider } from "./components/cell/monaco/KernelCompletionProvider.js";
import { TypeaheadCompletionProvider } from "./components/cell/monaco/TypeaheadCompletionProvider.js";
import { ElementMeasurer } from "./components/common/ElementMeasurer";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { Favicon } from "./components/common/Favicon";
import {
  HexToasterOverlays,
  HexToasterProvider,
} from "./components/common/Toasts";
import { Org } from "./generated/graphqlTypes";
import { useAttribution } from "./hooks/useAttribution";
import { UrlCleaner } from "./hooks/useUrlCleaner.js";
import { ORG_ID, getUrlOrg } from "./orgs";
import {
  OrgsClientDocument,
  OrgsClientQuery,
  OrgsPickerClientDocument,
  OrgsPickerClientQuery,
} from "./orgs.generated";
import { createStore } from "./redux/store";
import AuthenticatedRouteRedirect from "./route/AuthenticatedRouteRedirect.js";
import {
  InvalidLinkRoute,
  LoggedInLinkRoute,
  WrongUserLinkRoute,
} from "./route/LinkRoutes";
import { LoginRoute } from "./route/LoginRoute";
import { MagicAuthCallbackRoute } from "./route/MagicAuthCallbackRoute";
import { MagicLinkSentRoute } from "./route/MagicLinkSentRoute";
import { Routes, deepPath } from "./route/routes";
import { SignupRoute } from "./route/SignupRoute";
import { WorkspacePickerRoute } from "./route/WorkspacePickerRoute";
import { GlobalStyle } from "./theme/common/GlobalStyle";
import ThemeWrapper, {
  SignedEmbeddingAppThemeOverrideWrapper,
} from "./ThemeWrapper";
import { usePerformanceStats } from "./usePerformanceStats";
import { ConnectionManagerProvider } from "./util/ConnectionManager";
import { webClientVersion } from "./util/data";
import { shouldIgnoreShortcut } from "./util/Keys";
import { getOriginOverrideUrlObject } from "./util/mcpOverrides";
import { reactLazyRetry } from "./util/reactLazyRetry";
import { TRACE_LOG } from "./util/TraceLog";
import { sendEvents, trackPage } from "./util/trackEvent.js";
import { useGetFavicon } from "./util/useGetFavicon.js";
import { BigQueryOAuthSuccessView } from "./views/BigQueryOAuthSuccessView.js";
import { CliAuthView } from "./views/cli-auth/CliAuthView.js";
import { DatabricksOAuthSuccessView } from "./views/DatabricksOAuthSuccessView.js";
import { GoogleDriveOauthCompleteView } from "./views/GoogleDriveOauthCompleteView";
import { LoadingView } from "./views/LoadingView";
import { LoginSuccessView } from "./views/LoginSuccessView";
import { McpServerOauthResultView } from "./views/McpServerOauthResultView";
import { NotionUnfurlAuthorizeView } from "./views/NotionUnfurlAuthorizeView";
import { SnowflakeOauthSuccessView } from "./views/SnowflakeOauthSuccessView";

const urlOrg = getUrlOrg();
const appHistory = urlOrg != null ? `/${urlOrg}` : undefined;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;

  background-color: ${({ theme }) =>
    theme.isMcpTheme ? "transparent" : theme.backgroundColor.DEFAULT};
`;

MouseTrap.prototype.stopCallback = shouldIgnoreShortcut;

const LazyOrgSelectorRoute = reactLazyRetry(
  () => import("./route/OrgSelectorRoute"),
);

const LazyHexRoutes = reactLazyRetry(
  () => import(/* webpackPrefetch: true */ "./route/HexRoutes"),
);

const LazySpellbookRoute = reactLazyRetry(
  () => import("./route/SpellbookRoute"),
);

const LazyEmbeddedAppTestPageRoute = reactLazyRetry(
  () => import("./route/EmbedAppTestRoute"),
);

const LazyMcpAppTestHarness = reactLazyRetry(
  () => import("./route/McpAppTestHarness"),
);

const LazyEmailRedirectedRoute = reactLazyRetry(
  () => import("./route/EmailRedirectedRoute"),
);

const LazyGitSetupRoute = reactLazyRetry(() => import("./route/GitSetupRoute"));

const PageViewWrapper: React.FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  // Tracks page view events via Rudderstack
  // Must be inserted between Router and Routes to track page changes
  const routerLocation = useLocation();

  useEffect(() => {
    trackPage();
  }, [routerLocation]);

  return <>{children}</>;
};

const LoginRedirect: React.FunctionComponent = () => {
  const attribution = useAttribution();
  return <Navigate replace={true} to={Routes.LOGIN.getUrl({ attribution })} />;
};

type AppProps = {
  /**
   * Whether the router in this component is being overridden by a higher level router
   */
  hasRouterOverride?: boolean;
  /**
   * Used to override the Apollo client for MCP apps
   */
  clientOverride?: ApolloClient<unknown>;
};

const App: React.FunctionComponent<AppProps> = ({
  clientOverride,
  hasRouterOverride,
}) => {
  const client = useMemo(() => {
    return clientOverride ?? createClient({});
  }, [clientOverride]);

  useEffect(() => {
    client.writeQuery<OrgsClientQuery>({
      query: OrgsClientDocument,
      data: {
        __typename: "Query",
        orgsClient: {
          __typename: "ClientOrgResponse",
          orgs: [],
          loaded: false,
        },
      },
    });

    const orgInitTraceId = createTraceId();
    const urlOverride = getOriginOverrideUrlObject();
    const base = urlOverride?.origin ?? "";

    TRACE_LOG.push({
      traceId: orgInitTraceId,
      status: "STARTING",
      name: "org init",
    });

    void fetch(`${base}/orgs`, {
      headers: {
        [TRACE_ID_HEADER]: orgInitTraceId,
        [AGENT_TYPE_HEADER]: AgentType.WEB_CLIENT,
        [AGENT_VERSION_HEADER]: webClientVersion ?? "<unknown>",
      },
    })
      .then(async (res) => {
        TRACE_LOG.push({
          traceId: orgInitTraceId,
          status: "OK",
          name: "org init",
        });
        const body: Omit<Org, "__typename">[] = await res.json();

        client.writeQuery<OrgsClientQuery>({
          query: OrgsClientDocument,
          data: {
            __typename: "Query",
            orgsClient: {
              __typename: "ClientOrgResponse",
              orgs: body.map((org) => ({ ...org, __typename: "Org" })),
              loaded: true,
            },
          },
        });
      })
      .catch((err) => {
        TRACE_LOG.push({
          traceId: orgInitTraceId,
          status: "ERROR",
          name: "org init",
        });
        throw err;
      });

    const orgPickerInitTraceId = createTraceId();

    TRACE_LOG.push({
      traceId: orgPickerInitTraceId,
      status: "STARTING",
      name: "org picker init",
    });

    void fetch(`${base}/workspace-picker-orgs`, {
      headers: {
        [TRACE_ID_HEADER]: orgPickerInitTraceId,
        [AGENT_TYPE_HEADER]: AgentType.WEB_CLIENT,
        [AGENT_VERSION_HEADER]: webClientVersion ?? "<unknown>",
      },
    })
      .then(async (res) => {
        TRACE_LOG.push({
          traceId: orgPickerInitTraceId,
          status: "OK",
          name: "org picker init",
        });
        const body = await res.json();

        // Ensure all Authed orgs match expected type
        const authenticatedUsers = body.authedOrgDetails
          .filter((data: UserOrgDetails) =>
            AuthenticatedUserOrgDetails.guard(data),
          )
          .map((data: AuthenticatedUserOrgDetails) => {
            return {
              id: data.userId,
              orgRole: data.orgRole,
              email: data.email,
              org: {
                __typename: "Org",
                id: data.id,
                displayName: data.displayName,
                userFacingId: data.userFacingId ?? null,
                // when querying for orgs we won't know when the user is requesting
                // for the SUPPORT_HEX_EMAIL so if usercount is undefined we just
                // set it to -1
                userCount: data.userCount ?? -1,
              },
              __typename: "User",
            };
          });

        // Ensure all unauthed orgs match expected type
        const unauthenticatedUsers = body.unAuthedOrgDetails
          .filter(
            (data: UserOrgDetails): data is UnAuthenticatedUserOrgDetails =>
              UnAuthenticatedUserOrgDetails.guard(data) &&
              !AuthenticatedUserOrgDetails.guard(data),
          )
          .map((data: UnAuthenticatedUserOrgDetails) => {
            return {
              __typename: "User",
              id: data.id,
              email: data.email,
              org: {
                __typename: "Org",
                id: data.id,
                displayName: data.displayName,
                ssoEnforced: data.ssoEnforced,
                userFacingId: data.userFacingId ?? null,
              },
            };
          });

        client.writeQuery<OrgsPickerClientQuery>({
          query: OrgsPickerClientDocument,
          data: {
            __typename: "Query",
            orgPickerClient: {
              __typename: "ClientOrgPickerResponse",
              loaded: true,
              authenticatedUsers,
              unauthenticatedUsers,
            },
          },
        });
      })
      .catch((err) => {
        TRACE_LOG.push({
          traceId: orgPickerInitTraceId,
          status: "ERROR",
          name: "org picker init",
        });
        throw err;
      });
  }, [client]);
  const ldClientRef = useRef<LDClient | null>(null);
  const ldClient = useLDClient();

  if (ldClient != null) {
    ldClientRef.current = ldClient;
  }
  const storeRef = useRef<ReturnType<typeof createStore>>(undefined);
  if (storeRef.current == null) {
    storeRef.current = createStore(ORG_ID, {
      client,
      getHexFlag: (flag: AppFeatureFlag) =>
        ldClientRef.current?.variation(flag) ?? DEFAULT_FLAGS[flag],
    });
  }

  const [router] = useState(() =>
    createBrowserRouter([{ path: "*", element: <Root client={client} /> }], {
      basename: appHistory,
    }),
  );

  // If there is a router override (eg a Router exists at a higher level), we don't need to
  // (and cannot) initialize a second router here, so we just return the Root component directly.
  if (hasRouterOverride) {
    return (
      <ReduxProvider store={storeRef.current}>
        <Root client={client} />
      </ReduxProvider>
    );
  }

  // we use RouterProvider so that useNavigate() will be stable
  // see https://github.com/remix-run/react-router/issues/10743#issuecomment-1659006452
  return (
    <ReduxProvider store={storeRef.current}>
      <RouterProvider router={router} />
    </ReduxProvider>
  );
};

const Root: React.FunctionComponent<{ client: ApolloClient<unknown> }> = ({
  client,
}) => {
  const favicon = useGetFavicon({ inProject: false });

  usePerformanceStats();

  useEffect(() => {
    // ensure that we send all track event data when users closes out of session.
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon#sending_analytics_at_the_end_of_a_session
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        sendEvents();
      }
    });
  }, []);

  return (
    <ErrorBoundary reloadPageOnTryAgain={true}>
      <AtomicControllerContext>
        <ApolloProvider client={client}>
          <ConnectionManagerProvider value={connectionManager}>
            <KernelCompletionProvider>
              <TypeaheadCompletionProvider>
                <ThemeWrapper>
                  <HelmetProvider>
                    <AppContainer>
                      <Helmet defaultTitle="Hex" />
                      <Favicon href={favicon} />
                      <GlobalStyle />
                      <UrlCleaner />
                      <ErrorBoundary reloadPageOnTryAgain={true}>
                        <Suspense fallback={<LoadingView />}>
                          <HexToasterProvider>
                            <SignedEmbeddingAppThemeOverrideWrapper>
                              <HexToasterOverlays />
                            </SignedEmbeddingAppThemeOverrideWrapper>
                            <ElementMeasurer />
                            <DndProvider backend={HTML5Backend}>
                              <PageViewWrapper>
                                {urlOrg == null ? (
                                  <RouterRoutes>
                                    <Route
                                      element={
                                        <AuthenticatedRouteRedirect
                                          toRoute={Routes.NEW_PROJECT}
                                        />
                                      }
                                      path={deepPath(Routes.NEW_PROJECT.path)}
                                    />
                                    <Route
                                      element={
                                        <AuthenticatedRouteRedirect
                                          toRoute={Routes.NEW_THREAD}
                                        />
                                      }
                                      path={deepPath(Routes.NEW_THREAD.path)}
                                    />
                                    <Route
                                      element={<MagicLinkSentRoute />}
                                      path={deepPath(
                                        `${Routes.SIGNUP.path}${Routes.MAGIC_LINK_SENT.path}`,
                                      )}
                                    />
                                    <Route
                                      element={<MagicLinkSentRoute />}
                                      path={deepPath(
                                        `${Routes.LOGIN.path}${Routes.MAGIC_LINK_SENT.path}`,
                                      )}
                                    />
                                    <Route
                                      element={<MagicAuthCallbackRoute />}
                                      path={deepPath(
                                        Routes.MAGIC_AUTH_CALLBACK.path,
                                      )}
                                    />
                                    <Route
                                      element={<WorkspacePickerRoute />}
                                      path={deepPath(
                                        Routes.WORKSPACE_PICKER.path,
                                      )}
                                    />
                                    <Route
                                      element={<WrongUserLinkRoute />}
                                      path={deepPath(
                                        Routes.LINK_WRONG_USER.path,
                                      )}
                                    />
                                    <Route
                                      element={<LoggedInLinkRoute />}
                                      path={deepPath(
                                        Routes.LINK_LOGGED_IN.path,
                                      )}
                                    />
                                    <Route
                                      element={<InvalidLinkRoute />}
                                      path={deepPath(Routes.LINK_INVALID.path)}
                                    />
                                    <Route
                                      element={<SignupRoute />}
                                      path={Routes.SIGNUP.path}
                                    />
                                    <Route
                                      element={<LazySpellbookRoute />}
                                      path={Routes.SPELLBOOK.path}
                                    />
                                    <Route
                                      element={<LazyEmbeddedAppTestPageRoute />}
                                      path={deepPath(
                                        Routes.EMBED_APP_TEST_PAGE.path,
                                      )}
                                    />
                                    {process.env.NODE_ENV === "development" && (
                                      <Route
                                        element={<LazyMcpAppTestHarness />}
                                        path={deepPath(
                                          Routes.MCP_APP_TEST_HARNESS.path,
                                        )}
                                      />
                                    )}
                                    <Route
                                      element={<LazyEmailRedirectedRoute />}
                                      path={deepPath(
                                        Routes.EMAIL_REDIRECTED.path,
                                      )}
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes.GITHUB_PACKAGE_CONNECT_SUCCESS
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes.GITHUB_PACKAGE_INSTALL_LANDING
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes
                                          .GITHUB_SERVER_PACKAGE_CONNECT_SUCCESS
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes
                                          .GITHUB_SERVER_PACKAGE_INSTALL_LANDING
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes.GITLAB_PACKAGE_INSTALL_LANDING
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes.GITHUB_SYNC_INSTALL_LANDING.path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes.GITHUB_SYNC_CONNECT_SUCCESS.path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes
                                          .GITHUB_SERVER_SYNC_INSTALL_LANDING
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes
                                          .GITLAB_SERVER_PACKAGE_INSTALL_LANDING
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes
                                          .GITHUB_SERVER_SYNC_CONNECT_SUCCESS
                                          .path
                                      }
                                    />
                                    <Route
                                      element={<LazyGitSetupRoute />}
                                      path={
                                        Routes.GITLAB_SYNC_INSTALL_LANDING.path
                                      }
                                    />
                                    <Route
                                      element={<McpServerOauthResultView />}
                                      path={Routes.MCP_SERVER_OAUTH_RESULT.path}
                                    />
                                    <Route
                                      element={<NotionUnfurlAuthorizeView />}
                                      path={Routes.NOTION_UNFURL_AUTHORIZE.path}
                                    />
                                    <Route
                                      element={<BigQueryOAuthSuccessView />}
                                      path={Routes.BIGQUERY_OAUTH_SUCCESS.path}
                                    />
                                    <Route
                                      element={<SnowflakeOauthSuccessView />}
                                      path={Routes.SNOWFLAKE_OAUTH_SUCCESS.path}
                                    />
                                    <Route
                                      element={<DatabricksOAuthSuccessView />}
                                      path={
                                        Routes.DATABRICKS_OAUTH_SUCCESS.path
                                      }
                                    />
                                    <Route
                                      element={<CliAuthView />}
                                      path={Routes.CLI_AUTHORIZE.path}
                                    />
                                    <Route
                                      element={<GoogleDriveOauthCompleteView />}
                                      path={
                                        Routes.GOOGLE_DRIVE_OAUTH_COMPLETE.path
                                      }
                                    />
                                    <Route
                                      element={<LazyOrgSelectorRoute />}
                                      path={deepPath(Routes.ORG_SELECTOR.path)}
                                    />
                                    <Route
                                      element={<LoginRoute />}
                                      path={deepPath(Routes.LOGIN.path)}
                                    />
                                    <Route
                                      element={<LoginSuccessView />}
                                      path={Routes.LOGIN_SUCCESS.path}
                                    />
                                    <Route
                                      element={<LoginRedirect />}
                                      path="*"
                                    />
                                  </RouterRoutes>
                                ) : (
                                  <LazyHexRoutes />
                                )}
                              </PageViewWrapper>
                            </DndProvider>
                          </HexToasterProvider>
                        </Suspense>
                      </ErrorBoundary>
                    </AppContainer>
                  </HelmetProvider>
                </ThemeWrapper>
              </TypeaheadCompletionProvider>
            </KernelCompletionProvider>
          </ConnectionManagerProvider>
        </ApolloProvider>
      </AtomicControllerContext>
    </ErrorBoundary>
  );
};

export default App;
